"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { useSession } from "next-auth/react";

export interface AssetFile {
  fileId: string;
  s3Key: string;
  fileType: 'upload' | 'generated' | 'edited';
  toolId: string | null;
  originalName: string;
  width: number;
  height: number;
  sizeBytes: number;
  mimeType: string;
  createdAt: string;
  signedUrl: string;
  thumbnailUrl: string;
  saved?: boolean; // For generated images - whether user saved it
}

interface AssetsContextType {
  assets: AssetFile[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  refreshAssets: () => Promise<void>;
}

const AssetsContext = createContext<AssetsContextType | undefined>(undefined);

export function AssetsProvider({ children }: { children: ReactNode }) {
  const { data: session, status: sessionStatus } = useSession();
  const [assets, setAssets] = useState<AssetFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  // Fetch user assets
  const fetchAssets = useCallback(async (isInitialLoad: boolean = true) => {
    if (!session?.user?.id) {
      setIsLoading(false);
      setAssets([]);
      return;
    }

    try {
      if (isInitialLoad) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }

      const response = await fetch(`/api/storage/files?limit=500`);

      if (!response.ok) {
        throw new Error('Failed to load assets');
      }

      const data = await response.json();
      const fetchedFiles = data.files || [];
      setAssets(fetchedFiles);
      setError(null);

      if (isInitialLoad) {
        hasLoadedRef.current = true;
        currentUserIdRef.current = session.user.id;
      }
    } catch (error: any) {
      console.error('Error fetching assets:', error);
      setError(error?.message || 'Failed to fetch assets');
      setAssets([]);
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, [session?.user?.id]);

  // Public method to refresh assets (background refresh)
  const refreshAssets = useCallback(async () => {
    if (!session?.user?.id) return;
    await fetchAssets(false);
  }, [fetchAssets, session?.user?.id]);

  // Listen for refresh events from other components
  useEffect(() => {
    const handleRefresh = () => {
      if (session?.user?.id) {
        refreshAssets();
      }
    };

    window.addEventListener('refreshAssets', handleRefresh);
    return () => {
      window.removeEventListener('refreshAssets', handleRefresh);
    };
  }, [session?.user?.id, refreshAssets]);

  // Load assets when session becomes available or user changes
  useEffect(() => {
    if (sessionStatus === 'loading') {
      setIsLoading(true);
      return;
    }

    if (sessionStatus === 'unauthenticated') {
      setAssets([]);
      setIsLoading(false);
      hasLoadedRef.current = false;
      currentUserIdRef.current = null;
      return;
    }

    // Session authenticated - fetch assets if we haven't loaded yet or user changed
    if (session?.user?.id) {
      const userIdChanged = currentUserIdRef.current !== session.user.id;
      if (!hasLoadedRef.current || userIdChanged) {
        fetchAssets(true);
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [session?.user?.id, sessionStatus, fetchAssets]);

  const value = {
    assets,
    isLoading,
    isRefreshing,
    error,
    refreshAssets,
  };

  return (
    <AssetsContext.Provider value={value}>
      {children}
    </AssetsContext.Provider>
  );
}

// Hook to use the assets context
export function useAssetsContext() {
  const context = useContext(AssetsContext);
  if (context === undefined) {
    throw new Error('useAssetsContext must be used within an AssetsProvider');
  }
  return context;
}

